// Notificaciones de escritorio del navegador para pedidos nuevos cuando la pestaña
// del panel está en SEGUNDO PLANO (el comercio cambió de tab/ventana). Es un refuerzo
// opcional de la alarma sonora + el NotificationCenter in-app: opt-in (requiere permiso
// del usuario) y a prueba de fallos — NUNCA debe romper la recepción de pedidos.

import { NOTIFICATION_EVENTS_MAP } from '@utils/notificationConstants';
import { showNativeNotification } from './showNativeNotification';

const isSupported = () => typeof window !== 'undefined' && 'Notification' in window;

let permissionRequested = false;

// Solicita el permiso de notificaciones UNA sola vez. Debe llamarse desde un gesto del
// usuario (click/touch) para que el navegador no bloquee el prompt. Idempotente.
export const ensureOrderNotifyPermission = (): void => {
  try {
    if (!isSupported() || permissionRequested) return;
    permissionRequested = true;
    if (Notification.permission !== 'default') return; // ya concedido o denegado
    Notification.requestPermission().catch(() => undefined);
  } catch {
    /* nunca romper por el permiso */
  }
};

// Dispara una notificación de escritorio si — y solo si — la pestaña está oculta y el
// usuario ya concedió permiso. Al hacer click, enfoca de vuelta la ventana del panel.
export const notifyNewOrder = (params: { title: string; body: string; orderId?: string }): void => {
  if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
  // Support, permission and the never-throw contract now live in the shared helper. The same
  // Android bug behind Sentry #125 (`new Notification` is an illegal constructor there, 6,112
  // events) was failing HERE too, only in silence: the old `catch` swallowed it, so no background
  // banner ever reached an Android device from the websocket path.
  //
  // `data` is what makes the Android click useful. On the service-worker fallback the click belongs
  // to the worker's `notificationclick`, which focuses the client and posts `data` back to the page;
  // `routeFromNotificationData` then navigates to the order. Without it the Android banner — which
  // this fix makes visible for the first time — would open and do nothing, which reads as more
  // broken than the silence it replaces. The shape has to match what the push payload uses, since
  // both click paths land on the same router.
  // Fire and forget: the helper never rejects, by contract.
  showNativeNotification({
    title: params.title,
    // tag fijo: si entran varios pedidos seguidos en background, se reemplaza la
    // notificación viva en vez de apilar decenas.
    options: {
      body: params.body,
      tag: 'plick-new-order',
      ...(params.orderId ? { data: { eventId: NOTIFICATION_EVENTS_MAP.newOrder.key, orderId: params.orderId } } : {}),
    },
    onClick: (notification) => {
      try {
        window.focus();
        notification.close();
      } catch {
        /* noop */
      }
    },
  });
};
